import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges, WritableSignal, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  BlockedPartner,
  RecommendedPartner,
  RecommendedPartnerOffer,
  ShelterPartner,
} from '../../shared-models/partners.model';
import { EditPartnerComponent } from '../edit-partner/edit-partner.component';
import { UnblockPartnerComponent } from '../unblock-partner/unblock-partner.component';
import { OfferExpirationTime, OffersService } from "../../services/offers.service";

@Component({
  selector: 'lib-organization-partners',
  templateUrl: './organization-partners.component.html',
  styleUrls: ['./organization-partners.component.css'],
})
export class OrganizationPartnersComponent implements OnChanges {
  @Input() categories: { key: string; value: string }[] = [];
  @Input() partners: ShelterPartner[] = [];
  @Input() blockedPartners: BlockedPartner[] = [];
  @Input() recommendedPartners: RecommendedPartner[] = [];

  @Output() unblockPartner = new EventEmitter<BlockedPartner>();
  @Output() updatePartner = new EventEmitter<ShelterPartner>();
  @Output() deletePartner = new EventEmitter<ShelterPartner>();

  expirationTimeList: WritableSignal<OfferExpirationTime[]> = signal([]);

  activeKey!: string;
  private recommendedMap = new Map<string, RecommendedPartner>();

  constructor(
    private sanitizer: DomSanitizer,
    private offcanvas: NgbOffcanvas,
    private offersService: OffersService
  ) {

    this.expirationTimeList.set(this.offersService.expirationTime);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['categories'] && this.categories.length) {
      this.activeKey = this.categories[0].key;
    }

    if (changes['recommendedPartners']) {
      this.recommendedMap.clear();
      for (const rp of this.recommendedPartners) {
        this.recommendedMap.set(rp.googlePlacesId, rp);
      }
    }
  }

  onCategoryChange(key: string) {
    this.activeKey = key;
  }

  openUnblock(b: BlockedPartner) {
    const position = window.matchMedia('(min-width: 768px)').matches
      ? 'end'
      : 'bottom';
    const ref = this.offcanvas.open(UnblockPartnerComponent, { position });
    ref.componentInstance.partnerName = b.name;
    ref.closed.subscribe((reason) => {
      if (reason === 'confirmed') {
        this.unblockPartner.emit(b);
      }
    });
  }
  isBlocked(p: ShelterPartner): boolean {
    return this.blockedPartners.some(
      (b) => b.googlePlacesId === p.googlePlacesId
    );
  }

  getBlockedReason(p: ShelterPartner): string {
    const b = this.blockedPartners.find(
      (b) => b.googlePlacesId === p.googlePlacesId
    );
    return b?.reason ?? '';
  }

  breakLabel(catValue: string): SafeHtml {
    let text =
      catValue === 'Emergency Veterinary Practice (ER Vet Practice)'
        ? 'Emergency Veterinary Practice'
        : catValue;
    if (text === 'Veterinary Practice') {
      return this.sanitizer.bypassSecurityTrustHtml(text);
    }
    if (text.includes('Veterinary Practice')) {
      text = text.replace('Veterinary Practice', 'Veterinary<br>Practice');
    }
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }

  getCategoryLabel(key: string): string {
    const raw = this.categories.find((c) => c.key === key)?.value ?? key;
    return this.stripParens(raw);
  }

  private stripParens(text: string): string {
    return text?.replace(/\s*\([^)]*\)$/, '') || '';
  }

  getSecondaryLabels(catKey: string | null): string {
    return catKey ? this.getCategoryLabel(catKey) : '';
  }

  getPartnersByCategory(key: string): ShelterPartner[] {
    return this.partners.filter(
      (p) => p.category === key || p.secondaryCategory === key
    );
  }

  formatAddress(addr: any): string {
    return [
      addr.streetLine1,
      addr.streetLine2,
      addr.city,
      addr.stateProvince,
      addr.zipCode,
      addr.country,
    ]
      .filter((x) => !!x && x.toString().trim())
      .join(', ');
  }

  isRecommended(p: ShelterPartner): boolean {
    return !!this.recommendedMap.get(p.googlePlacesId)?.recommended;
  }

  getOffers(p: ShelterPartner): RecommendedPartnerOffer[] {
    return this.recommendedMap.get(p.googlePlacesId)?.offers || [];
  }

  openEdit(p: ShelterPartner) {
    const position = window.matchMedia('(min-width: 768px)').matches
      ? 'end'
      : 'bottom';
    const ref = this.offcanvas.open(EditPartnerComponent, { position });
    const rp = this.recommendedMap.get(p.googlePlacesId);

    ref.componentInstance.partner = { ...p };
    ref.componentInstance.recommended = rp?.recommended ?? false;
    ref.componentInstance.offers = rp?.offers ? [...rp.offers] : [];
    ref.componentInstance.categories = this.categories;

    ref.closed.subscribe((result) => {
      if (result?.action === 'save') {
        p.category = result.partner.category;
        p.secondaryCategory = result.partner.secondaryCategory;
        if (rp) {
          rp.recommended = result.recommended;
          rp.offers = result.offers;
        }
        this.updatePartner.emit(p);
      }
    });
  }

  getExpirationLabel(days: number): string {
    const entry = this.expirationTimeList().find((e) => +e.value === days);
    const text = entry ? entry.label : `${days} days`;
    return `(${text})`;
  }
}
